import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import * as d3 from "d3";
import "./chart.css";
import { MdLabel, MdLabelOff } from "react-icons/md";

const GroupedBarChart = forwardRef(({ xData, yData }, ref) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const [chartWidth, setChartWidth] = useState(window.innerWidth * 0.9);
  const [chartHeight, setChartHeight] = useState(window.innerHeight * 0.7);
  const [visibleFields, setVisibleFields] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [legendSearch, setLegendSearch] = useState("");

  useImperativeHandle(ref, () => ({
    getUpdatedChartData: () => [...yData],
  }));

  // Handle resize
  useEffect(() => {
    const updateChartSize = () => {
      setChartWidth(window.innerWidth * 0.9);
      setChartHeight(window.innerHeight * 0.7);
    };
    updateChartSize();
    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, []);

  // Initialize visible fields
  useEffect(() => {
    if (!xData || !yData || xData.length === 0 || yData.length === 0) return;
    setVisibleFields(yData.map(d => d.field));
  }, [xData, yData]);

  // Main chart rendering
  useEffect(() => {
    if (!xData || !yData || xData.length === 0 || yData.length === 0) return;

    const margin = { top: 60, right: 20, bottom: 60, left: 60 };
    const height = chartHeight;
    const width = chartWidth;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width + margin.left + margin.right)
       .attr("height", height + margin.top + margin.bottom)
       .style("background-color", "#F2F2F2");

    const filteredYData = yData.filter(d => visibleFields.includes(d.field));
    const keys = filteredYData.map(d => d.field);

    // Compute dynamic min and max for y-axis
    const allValues = filteredYData.flatMap(d => d.values.map(v => v ?? 0));
    const minY = d3.min(allValues);
    const maxY = d3.max(allValues);

    const x0 = d3.scaleBand()
      .domain(xData)
      .range([margin.left, width - margin.right])
      .paddingInner(0.2);

    const x1 = d3.scaleBand()
      .domain(keys)
      .range([0, x0.bandwidth()])
      .padding(0.05);
      
const yScale = d3.scaleLinear()
  .domain([Math.min(0, minY*1.1), maxY*1.1])
  .range([height - margin.bottom, margin.top]);


    // Axes
    const xAxis = d3.axisBottom(x0);
    const yAxis = d3.axisLeft(yScale);

    svg.append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(xAxis)
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    svg.append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${margin.left},0)`)
      .call(yAxis)
      .selectAll("text")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    // Zero line (only if 0 is in range)
    if (minY < 0 && maxY > 0) {
      svg.append("line")
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", yScale(0))
        .attr("y2", yScale(0))
        .attr("stroke", "black")
        .attr("stroke-width", 1.5);
    }

    // Tooltip
    const tooltip = d3.select(tooltipRef.current)
      .style("opacity", 0)
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("background-color", "rgba(0,0,0,0.6)")
      .style("color", "white")
      .style("padding", "5px")
      .style("border-radius", "4px");

    // Bar groups
    const barGroups = svg.append("g")
      .selectAll(".bar-group")
      .data(xData.map((date, i) => ({ date, i })))
      .enter()
      .append("g")
      .attr("class", "bar-group")
      .attr("transform", d => `translate(${x0(d.date)},0)`);

    barGroups.selectAll("rect")
      .data(d => filteredYData.map(series => ({
        key: series.field,
        value: series.values[d.i] ?? 0,
        color: series.color,
        date: d.date,
      })))
      .enter()
      .append("rect")
      .attr("x", d => x1(d.key))
      .attr("y", d => d.value >= 0 ? yScale(d.value) : yScale(0))
      .attr("width", x1.bandwidth())
      .attr("height", d => Math.abs(yScale(d.value) - yScale(0)))
      .attr("fill", d => d.color);

    // Overlay for tooltip (hover anywhere in x-axis group)
    svg.selectAll(".hover-area")
      .data(xData.map((date, i) => ({ date, i })))
      .enter()
      .append("rect")
      .attr("class", "hover-area")
      .attr("x", d => x0(d.date))
      .attr("y", margin.top)
      .attr("width", x0.bandwidth())
      .attr("height", height - margin.top - margin.bottom)
      .attr("fill", "transparent")
      .on("mousemove", (event, d) => {
        const [x, y] = d3.pointer(event, svg.node());

        const content = filteredYData.map(series => {
          const value = series.values[d.i] ?? 0;
          return `
            <div style="display:flex;align-items:center;margin-top:2px;">
              <div style="width:10px;height:10px;background-color:${series.color};margin-right:5px;border:1px solid white;"></div>
              <span style="font-size:13px;font-weight:bold;">${series.field}:</span>
              <span style="margin-left:5px;font-size:13px;">${value.toLocaleString()}</span>
            </div>
          `;
        }).join("");

        tooltip.style("opacity", 1)
          .style("left", `${x + 10}px`)
          .style("top", `${y - 10}px`)
          .html(`
            <div style="color:white;font-size:14px;font-weight:bold;">${d.date}</div>
            ${content}
          `);
      })
      .on("mouseleave", () => tooltip.style("opacity", 0));

  }, [xData, yData, chartWidth, chartHeight, visibleFields]);

  return (
    <div className="chart-container" style={{ position: "relative", overflow: "visible" }}>
      <button
        onClick={() => setShowLegend(!showLegend)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          padding: "6px 12px",
          backgroundColor: "transparent",
          color: "black",
          borderRadius: "4px",
          cursor: "pointer"
        }}
      >
        {showLegend ? <MdLabelOff size={24} /> : <MdLabel size={24} />}
      </button>

      {showLegend && (
        <div
          className="legend-sidebar"
          style={{
            position: "absolute",
            top: 50,
            right: 10,
            width: "250px",
            maxHeight: "400px",
            backgroundColor: "#fff",
            boxShadow: "0 0 10px rgba(0,0,0,0.2)",
            padding: "10px",
            overflowY: "auto",
            zIndex: 9,
          }}
        >
          <input
            type="text"
            value={legendSearch}
            onChange={(e) => setLegendSearch(e.target.value)}
            placeholder="Search..."
            style={{
              width: "100%",
              padding: "6px 8px",
              marginBottom: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />

          <div>
            {yData
              .filter(d => d.values.some(v => v !== 0))
              .filter(d => d.field.toLowerCase().includes(legendSearch.toLowerCase()))
              .map(d => (
                <div
                  key={d.field}
                  onClick={() =>
                    setVisibleFields(prev =>
                      prev.includes(d.field)
                        ? prev.filter(f => f !== d.field)
                        : [...prev, d.field]
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: d.color,
                      marginRight: "10px",
                    }}
                  />
                  <span
                    style={{
                      textDecoration: visibleFields.includes(d.field) ? "none" : "line-through",
                    }}
                  >
                    {d.field}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      <svg ref={svgRef}></svg>
      <div ref={tooltipRef}></div>
    </div>
  );
});

export default GroupedBarChart;
